import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// otpauth is pure-JS and Worker-compatible
import { Secret, TOTP } from "otpauth";

const ISSUER = "Want To Buy";

function genBackupCodes(): string[] {
  const out: string[] = [];
  const buf = new Uint8Array(8 * 8);
  crypto.getRandomValues(buf);
  const alpha = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  for (let i = 0; i < 8; i++) {
    let s = "";
    for (let j = 0; j < 8; j++) s += alpha[buf[i * 8 + j] % alpha.length];
    out.push(`${s.slice(0, 4)}-${s.slice(4)}`);
  }
  return out;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Start enrollment: creates an unverified factor and returns the QR URI. */
export const enrollTotp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId, claims } = context;
    const secret = new Secret({ size: 20 });
    const totp = new TOTP({
      issuer: ISSUER,
      label: (claims.email as string | undefined) ?? userId,
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret,
    });
    const { error } = await supabase
      .from("user_totp_factors")
      .upsert(
        {
          user_id: userId,
          secret: secret.base32,
          verified: false,
          backup_codes: [],
          created_at: new Date().toISOString(),
          verified_at: null,
        },
        { onConflict: "user_id" },
      );
    if (error) throw new Error(error.message);
    return {
      otpauthUri: totp.toString(),
      secret: secret.base32,
    };
  });

/** Confirm enrollment: verify a code from the user's authenticator app. */
export const verifyTotpEnrollment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ code: z.string().regex(/^\d{6}$/) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase
      .from("user_totp_factors")
      .select("secret, verified")
      .eq("user_id", userId)
      .maybeSingle();
    if (error || !row) throw new Error("No enrollment in progress");

    const totp = new TOTP({
      issuer: ISSUER,
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: Secret.fromBase32(row.secret),
    });
    const delta = totp.validate({ token: data.code, window: 1 });
    if (delta === null) throw new Error("Invalid code");

    const backupCodes = genBackupCodes();
    const hashed = await Promise.all(backupCodes.map(sha256Hex));
    const now = new Date().toISOString();
    const { error: updErr } = await supabase
      .from("user_totp_factors")
      .update({
        verified: true,
        verified_at: now,
        backup_codes: hashed,
        session_verified_at: now,
      })
      .eq("user_id", userId);
    if (updErr) throw new Error(updErr.message);
    return { backupCodes };
  });

/** Verify a sign-in challenge code. Called from /auth after the password check. */
export const verifyTotpChallenge = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        code: z.string().min(6).max(12),
        backup: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("user_totp_factors")
      .select("secret, verified, backup_codes")
      .eq("user_id", userId)
      .maybeSingle();
    if (!row?.verified) throw new Error("2FA not enrolled");

    const now = new Date().toISOString();

    if (data.backup) {
      const normalized = data.code.trim().toUpperCase();
      const hashed = await sha256Hex(normalized);
      if (!row.backup_codes.includes(hashed)) throw new Error("Invalid backup code");
      const remaining = row.backup_codes.filter((c) => c !== hashed);
      await supabase
        .from("user_totp_factors")
        .update({ backup_codes: remaining, last_used_at: now, session_verified_at: now })
        .eq("user_id", userId);
      return { ok: true, backupRemaining: remaining.length };
    }

    if (!/^\d{6}$/.test(data.code)) throw new Error("Invalid code format");
    const totp = new TOTP({
      issuer: ISSUER,
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: Secret.fromBase32(row.secret),
    });
    const delta = totp.validate({ token: data.code, window: 1 });
    if (delta === null) throw new Error("Invalid code");
    await supabase
      .from("user_totp_factors")
      .update({ last_used_at: now, session_verified_at: now })
      .eq("user_id", userId);
    return { ok: true };
  });

/** Used by the auth gate to decide whether a 2FA challenge is required
 *  for the *current* sign-in session. Returns true when the user is
 *  enrolled AND has not satisfied a TOTP challenge since their most
 *  recent sign-in. */
export const checkTotpSessionRequired = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("user_totp_factors")
      .select("verified, session_verified_at")
      .eq("user_id", userId)
      .maybeSingle();
    if (!row?.verified) return { required: false };

    const { data: userRes } = await supabase.auth.getUser();
    const lastSignIn = userRes.user?.last_sign_in_at ?? null;
    if (!lastSignIn) return { required: true };
    if (!row.session_verified_at) return { required: true };
    return {
      required: new Date(row.session_verified_at).getTime() < new Date(lastSignIn).getTime(),
    };
  });

export const getMyTotpStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("user_totp_factors")
      .select("verified, verified_at, last_used_at")
      .eq("user_id", userId)
      .maybeSingle();
    return {
      enrolled: !!data?.verified,
      verifiedAt: data?.verified_at ?? null,
      lastUsedAt: data?.last_used_at ?? null,
    };
  });

export const disableTotp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("user_totp_factors")
      .delete()
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const regenerateBackupCodes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const codes = genBackupCodes();
    const hashed = await Promise.all(codes.map(sha256Hex));
    const { error } = await supabase
      .from("user_totp_factors")
      .update({ backup_codes: hashed })
      .eq("user_id", userId)
      .eq("verified", true);
    if (error) throw new Error(error.message);
    return { backupCodes: codes };
  });
